export const OTHER_SIZE_SCHEMA = "Other";

const UNIT_AS_SCHEMA = [
  "cm",
  "centimeter",
  "centimeters",
  "in",
  "inch",
  "inches",
];

interface MappedSizeData {
  schema?: string | null;
  value?: string | null;
  mappings?: Array<{ schema?: string | null; value?: string | null }> | null;
}

export function isSchemaActuallyUnit(schema?: string): boolean {
  if (!schema) return false;
  return UNIT_AS_SCHEMA.includes(schema.trim().toLowerCase());
}

export function normalizeSchema(schema?: string): string {
  if (!schema) return OTHER_SIZE_SCHEMA;
  return schema.toLowerCase() === "other" ? OTHER_SIZE_SCHEMA : schema;
}

// Derive a human-readable label from a bare Constructor facet-option id.
//
// A size id is `{schema-slug}-{name-slug}` — the lowercased schema joined to the
// lowercased name (e.g. `eu-23s||23S||EU||` has id "eu-23s"). When a facet option
// is "bare" (event-engine hasn't synced its `data`/`display_name`) the UI would
// otherwise render this raw id. Stripping the schema prefix and upper-casing the
// remainder reconstructs the label ("eu-24s" -> "24S", "cm-105" -> "105",
// "waist/length-33/34" -> "33/34"), matching how a populated option renders.
//
// Split on the FIRST dash only: a schema may contain a slash ("waist/length")
// but never a dash, while a name can ("37l/38l"), so the first dash is always
// the schema/name seam. This is only a fallback — the real fix is event-engine
// syncing the option metadata; see docs/product/sizing.md.
export function prettifySizeId(id: string): string {
  if (!id) return id;
  const seam = id.indexOf("-");
  const label = seam === -1 ? id : id.slice(seam + 1);
  return label.toUpperCase();
}

export function normalizeUnit(unit?: string): string | undefined {
  if (!unit) return undefined;
  const u = unit.trim().toLowerCase();
  if (u === "" || u === "other" || u === "na") return undefined;
  if (u === "centimeter" || u === "centimeters" || u === "cm") return "cm";
  if (u === "inch" || u === "inches" || u === "in") return "in";
  return unit;
}

export function buildGroupKey(opts: {
  schema: string;
  unit?: string;
  gender?: string;
}) {
  const schema = normalizeSchema(opts.schema);
  const unit = normalizeUnit(opts.unit);
  const gender = opts.gender?.toLowerCase();
  return [schema, unit || "", gender || ""].join("|");
}

export function buildGroupTitle(opts: {
  schema: string;
  unit?: string;
  sizeLabel?: string;
}) {
  const schema = normalizeSchema(opts.schema);
  const unit = normalizeUnit(opts.unit);
  const sizeLabel = opts.sizeLabel || "Size";

  if (isSchemaActuallyUnit(opts.schema)) {
    const normalizedSchemaAsUnit = normalizeUnit(opts.schema);
    return `${sizeLabel} (${normalizedSchemaAsUnit})`;
  }

  return unit ? `${schema} (${unit})` : schema;
}

export function unitRank(unit?: string): number {
  const u = normalizeUnit(unit);
  if (!u) return 0;
  if (u === "cm") return 1;
  if (u === "in") return 2;
  // push unknown units after known ones, keep consistent order
  return 10;
}

export function getEffectiveUnit(
  schema: string | null | undefined,
  unit: string | null | undefined
): string | undefined {
  if (unit && unit.trim() !== "") return normalizeUnit(unit);
  if (schema && isSchemaActuallyUnit(schema)) return normalizeUnit(schema);
  return undefined;
}

export function formatSizeValue(
  value: string,
  schema: string | null | undefined,
  unit: string | null | undefined,
  showUnitInValue = false
): string {
  const normalizedUnit = normalizeUnit(unit || undefined);
  const schemaIsUnit = isSchemaActuallyUnit(schema || undefined);
  const hasRealUnit = normalizedUnit || schemaIsUnit;

  if (hasRealUnit) {
    if (showUnitInValue) {
      const unitToShow =
        normalizedUnit ||
        (schemaIsUnit ? normalizeUnit(schema || undefined) : undefined);
      return unitToShow ? `${value} ${unitToShow}` : value;
    }
    return value;
  }

  if (!schema || schema.toUpperCase() === "INT") {
    return value;
  }
  return `${schema} ${value}`;
}

export function countSchemas(mappedSize?: MappedSizeData | null): number {
  if (!mappedSize) return 0;

  const schemas = new Set<string>();
  if (mappedSize.schema) schemas.add(mappedSize.schema);
  mappedSize.mappings?.forEach((mapping) => {
    if (mapping.schema) schemas.add(mapping.schema);
  });

  return schemas.size;
}

export function showMappedSizeSelector(
  variations: Array<{
    mapped_size?: MappedSizeData | null;
    stock_quantity?: number;
  }>
): boolean {
  const withMappedSize = variations.filter(
    (v) => v.mapped_size && (v.stock_quantity ?? 0) > 0
  );
  if (withMappedSize.length === 0) return false;
  return withMappedSize.some((v) => countSchemas(v.mapped_size) > 1);
}
